/**
 * Pure helpers for JsonTreeView keyboard navigation (Slice 10 — L4).
 *
 * Lifted out of JsonTreeView.tsx so the component file stays under the
 * 200-line ceiling and so the walker can be unit-tested directly without
 * mounting React. Pattern matches Slice 6/8 (sibling extraction silences
 * `react-refresh/only-export-components`).
 */

import { jsonTreeChildPath } from './jsonTreeEdit';
export type JsonNodeKind =
  | 'object'
  | 'array'
  | 'string'
  | 'number'
  | 'boolean'
  | 'null';

export function kindOf(value: unknown): JsonNodeKind {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  const t = typeof value;
  if (t === 'object') return 'object';
  if (t === 'string') return 'string';
  if (t === 'number') return 'number';
  if (t === 'boolean') return 'boolean';
  return 'string';
}

/** Override map + base depth threshold; resolved by `isPathExpanded`. */
export type ExpandState = {
  overrides: Map<string, boolean>;
  defaultExpandDepth: number;
};

/**
 * Resolve a container's expanded state. Explicit overrides (set by the
 * user via toggle clicks or keyboard arrows) win; otherwise fall back to
 * the depth-based default.
 */
export function isPathExpanded(
  state: ExpandState,
  path: string,
  depth: number,
): boolean {
  const override = state.overrides.get(path);
  if (override !== undefined) return override;
  return depth < state.defaultExpandDepth;
}

export type VisibleEntry = {
  path: string;
  depth: number;
  parentPath: string | null;
  isContainer: boolean;
  /**
   * `true` only when the container is non-empty AND currently expanded.
   * Empty containers (`{}` / `[]`) are reported with `isExpanded=false`
   * because they don't reveal any children.
   */
  isExpanded: boolean;
  isEmpty: boolean;
};

/**
 * Depth-first traversal yielding every container path in the value tree —
 * regardless of current expansion. Used by the Expand-all / Collapse-all
 * toolbar to seed `overrides` for every container in one shot.
 */
export function collectContainerPaths(
  value: unknown,
  basePath = 'root',
): string[] {
  const out: string[] = [];
  walk(value, basePath);
  return out;

  function walk(v: unknown, path: string): void {
    const kind = kindOf(v);
    if (kind !== 'object' && kind !== 'array') return;
    const entries: Array<[string | number, unknown]> =
      kind === 'array'
        ? (v as unknown[]).map((child, i) => [i, child] as [number, unknown])
        : Object.entries(v as Record<string, unknown>);
    if (entries.length === 0) return;
    out.push(path);
    for (const [k, child] of entries) {
      walk(child, jsonTreeChildPath(path, k));
    }
  }
}

/**
 * Depth-first traversal yielding every treeitem path that is currently
 * visible — i.e. all ancestor containers are expanded. Used by the
 * keyboard handler to compute next/prev focus targets.
 */
export function walkVisible(
  value: unknown,
  state: ExpandState,
  basePath = 'root',
): VisibleEntry[] {
  const out: VisibleEntry[] = [];
  walk(value, basePath, 0, null);
  return out;

  function walk(
    v: unknown,
    path: string,
    depth: number,
    parentPath: string | null,
  ): void {
    const kind = kindOf(v);
    const isContainer = kind === 'object' || kind === 'array';
    const entries: Array<[string | number, unknown]> = isContainer
      ? kind === 'array'
        ? (v as unknown[]).map((child, i) => [i, child] as [number, unknown])
        : Object.entries(v as Record<string, unknown>)
      : [];
    const isEmpty = isContainer && entries.length === 0;
    const expanded = isContainer && !isEmpty && isPathExpanded(state, path, depth);
    out.push({
      path,
      depth,
      parentPath,
      isContainer,
      isExpanded: expanded,
      isEmpty,
    });
    if (expanded) {
      for (const [k, child] of entries) {
        walk(child, jsonTreeChildPath(path, k), depth + 1, path);
      }
    }
  }
}
